import React from 'react';
import { Cpu, Database, Server, Lock, FileText, Search } from 'lucide-react';
import { techStack } from '../data/mock';

const TechSection = () => {
  const architectureFeatures = [
    {
      icon: FileText,
      title: "Doküman İşleme",
      items: ["PDF Parser", "DOCX Extractor", "TXT/Markdown", "OCR Desteği"],
      color: "blue"
    },
    {
      icon: Search,
      title: "Akıllı Arama",
      items: ["Semantic Search", "Vektör Arama", "Tam Metin Arama", "Filtreleme"],
      color: "cyan"
    },
    {
      icon: Cpu,
      title: "LLM Entegrasyonu",
      items: ["OpenAI GPT", "Google Gemini", "Ollama (Local)", "Model Seçimi"],
      color: "violet"
    },
    {
      icon: Database,
      title: "Veritabanı",
      items: ["SQL Server", "PostgreSQL", "MySQL", "Oracle / SAP HANA"],
      color: "emerald"
    }
  ];

  return (
    <section id="tech" className="py-24 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-blue-500/20 text-blue-400 text-sm font-semibold mb-4">
            Teknoloji
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
            Güçlü Teknoloji
            <span className="text-blue-400"> Altyapısı</span>
          </h2>
          <p className="max-w-2xl mx-auto text-lg text-slate-400">
            Modern AI teknolojileri ve kurumsal veritabanı sistemleri ile entegre çalışan
            ölçeklenebilir mimari.
          </p>
        </div>

        {/* Architecture Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {architectureFeatures.map((feature, idx) => {
            const colorClasses = {
              blue: "border-blue-500/30 text-blue-400",
              cyan: "border-cyan-500/30 text-cyan-400",
              violet: "border-violet-500/30 text-violet-400",
              emerald: "border-emerald-500/30 text-emerald-400"
            };
            const iconBg = {
              blue: "bg-blue-500/20",
              cyan: "bg-cyan-500/20",
              violet: "bg-violet-500/20",
              emerald: "bg-emerald-500/20"
            };
            const dotColor = {
              blue: "bg-blue-400",
              cyan: "bg-cyan-400",
              violet: "bg-violet-400",
              emerald: "bg-emerald-400"
            };

            return (
              <div
                key={idx}
                className={`p-6 rounded-2xl bg-white/5 backdrop-blur-sm border ${colorClasses[feature.color].split(' ')[0]} hover:bg-white/10 transition-all duration-300`}
              >
                <div className={`w-12 h-12 rounded-xl ${iconBg[feature.color]} flex items-center justify-center mb-4`}>
                  <feature.icon className={`w-6 h-6 ${colorClasses[feature.color].split(' ')[1]}`} />
                </div>
                <h3 className="text-lg font-bold text-white mb-4">{feature.title}</h3>
                <ul className="space-y-2">
                  {feature.items.map((item, i) => (
                    <li key={i} className="text-sm text-slate-400 flex items-center gap-2">
                      <span className={`w-1.5 h-1.5 rounded-full ${dotColor[feature.color]}`} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Integration Support */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* LLM Support */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Cpu className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Çoklu LLM Desteği</h3>
                <p className="text-slate-400 text-sm">İhtiyacınıza göre model seçin</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {techStack.filter(t => t.category === 'LLM').map((tech, idx) => (
                <div
                  key={idx}
                  className="px-4 py-2 rounded-lg bg-white/10 text-white font-medium hover:bg-blue-500/30 transition-colors cursor-default"
                >
                  {tech.name}
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-500 mt-4">
              * Ollama ile tamamen on-premise çözüm mümkün
            </p>
          </div>

          {/* Database Support */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <Database className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Veritabanı Entegrasyonu</h3>
                <p className="text-slate-400 text-sm">Mevcut sistemlerinize bağlanın</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {techStack.filter(t => t.category === 'Database').map((tech, idx) => (
                <div
                  key={idx}
                  className="px-4 py-2 rounded-lg bg-white/10 text-white font-medium hover:bg-emerald-500/30 transition-colors cursor-default"
                >
                  {tech.name}
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-500 mt-4">
              * Read-only bağlantı ile güvenli sorgulama
            </p>
          </div>
        </div>

        {/* Security Features */}
        <div className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 rounded-2xl border border-blue-500/30 p-8 md:p-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Kurumsal Güvenlik
              </h3>
              <p className="text-slate-300 mb-6">
                Endüstri standardı güvenlik protokolleri ile verileriniz ve dokümanlarınız
                her zaman koruma altında.
              </p>
              <ul className="space-y-3 text-slate-300">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-emerald-400 rounded-full" />
                  Bcrypt ile parola hashleme
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-emerald-400 rounded-full" />
                  JWT tabanlı token yapısı
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-emerald-400 rounded-full" />
                  Tüm işlemlerin audit logları
                </li>
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Server, label: 'JWT Auth', desc: 'Güvenli token' },
                { icon: Lock, label: 'Bcrypt', desc: 'Şifreleme' },
                { icon: Database, label: 'RBAC', desc: 'Rol bazlı erişim' },
                { icon: Cpu, label: 'SSO', desc: 'LDAP/AD' }
              ].map((item, idx) => (
                <div key={idx} className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <item.icon className="w-6 h-6 text-blue-400 mb-2" />
                  <div className="text-white font-semibold">{item.label}</div>
                  <div className="text-slate-400 text-sm">{item.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechSection;
